import { useCallback, useState } from 'react';

import { PreferredDriverFormModal } from './PreferredDriverFormModal';
import { useAppDispatch } from 'src/app/store/hooks';
import { ManageCell as BaseManageCell } from 'src/common/DataGrid/ManageCell';
import type {
  DriverRead,
  PreferredDriverRead,
} from 'src/common/external/bambi-api/bambiApi';
import { usePassengersPreferredDriversDestroyMutation } from 'src/common/external/bambi-api/bambiApi';
import { ConfirmModal } from 'src/common/modals/ConfirmModal';
import { useModal } from 'src/common/modals/useModal';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

interface ManageCellProps {
  preferredDriver: PreferredDriverRead;
  passengerId: string;
  passengerName: string;
  drivers: DriverRead[];
}

export function ManageCell({
  preferredDriver,
  passengerId,
  passengerName,
  drivers,
}: ManageCellProps) {
  const dispatch = useAppDispatch();
  const { open: editOpen, setOpen: setEditOpen } = useModal();
  const {
    open: deleteOpen,
    setOpen: setDeleteOpen,
    closeModal: closeDelete,
  } = useModal();
  const [deleteError, setDeleteError] = useState<string | undefined>();

  const [deletePreference, { isLoading: isDeleting }] =
    usePassengersPreferredDriversDestroyMutation();

  const handleSetDeleteOpen = useCallback(
    (open: boolean) => {
      if (!open) setDeleteError(undefined);
      setDeleteOpen(open);
    },
    [setDeleteOpen]
  );

  const handleCancelDelete = useCallback(() => {
    setDeleteError(undefined);
    closeDelete();
  }, [closeDelete]);

  const handleDelete = useCallback(async () => {
    setDeleteError(undefined);
    try {
      await deletePreference({
        passengerId,
        id: preferredDriver.id,
      }).unwrap();
      dispatch(show({ title: 'Driver rule deleted', type: 'success' }));
      closeDelete();
    } catch (e) {
      setDeleteError(formatServerError(e));
    }
  }, [
    deletePreference,
    passengerId,
    preferredDriver.id,
    dispatch,
    closeDelete,
  ]);

  return (
    <>
      <BaseManageCell
        items={[
          {
            label: 'Edit',
            icon: 'pencil',
            onClick: () => setEditOpen(true),
          },
          {
            label: 'Delete',
            icon: 'trash',
            onClick: () => setDeleteOpen(true),
          },
        ]}
      />
      <PreferredDriverFormModal
        open={editOpen}
        setOpen={setEditOpen}
        passengerId={passengerId}
        passengerName={passengerName}
        preferredDriver={preferredDriver}
        drivers={drivers}
      />
      <ConfirmModal
        open={deleteOpen}
        setOpen={handleSetDeleteOpen}
        onCancel={handleCancelDelete}
        onConfirm={handleDelete}
        title="Delete Driver Rule"
        description="Are you sure you want to delete this driver rule? This cannot be undone."
        confirmText="Delete"
        loading={isDeleting}
        error={deleteError}
        mode="destructive"
      />
    </>
  );
}
